import React, { useState, useEffect } from 'react'
import { useUser } from 'util/UserContext'
import UsernameModal from 'components/User/UsernameModal'
import UserAvatarModal from 'components/User/UserAvatarModal'
import UserAboutBriefModal from 'components/User/UserAboutBriefModal'
import UserAboutIssueModal from 'components/User/UserAboutIssueModal'
import UserDisplaynameModal from 'components/User/UserDisplaynameModal'
import UserAboutLinksModal from 'components/User/UserAboutLinksModal'
import UserAboutBackgroundModal from 'components/User/UserAboutBackgroundModal'
import ProfileSettingsButton from 'ui/ProfileSettingsButton'
import { Loader2 } from 'lucide-react'
import { getIssueRankings } from 'util/userUtils'
import UserPerspectiveForm from './UserPerspectiveForm'

const UserSettingsProfile: React.FC = () => {
    const { currentUser } = useUser()
    const [showUsernameModal, setShowUsernameModal] = useState<boolean>(false)
    const [showDisplaynameModal, setShowDisplaynameModal] = useState<boolean>(false)
    const [showAvatarModal, setShowAvatarModal] = useState<boolean>(false)
    const [showBriefModal, setShowBriefModal] = useState<boolean>(false)
    const [showLinksModal, setShowLinksModal] = useState<boolean>(false)
    const [showIssueModal, setShowIssueModal] = useState<boolean>(false)
    const [showBackgroundModal, setShowBackgroundModal] = useState<boolean>(false)
    const [issueCount, setIssueCount] = useState<number>(0)
    const [loadingRankings, setLoadingRankings] = useState<boolean>(false)
    const [showPerspectiveModal, setShowPerspectiveModal] = useState<boolean>(false);

    // Auth-unauth redirect is handled by the parent `UserSettings`
    // route via `RequireAuth`.

    useEffect(() => {
        const fetchIssueCount = async () => {
            setLoadingRankings(true)
            const response = await getIssueRankings(currentUser?.id)
            setIssueCount(response.data.length)
            setLoadingRankings(false)
        }
        fetchIssueCount()
    }, [currentUser?.id])
    return (
        <div className="p-2">
                {/* General Section */}
                <div className="form-control space-y-2">
                    <label className="form-label text-base">General</label>
                    <ProfileSettingsButton
                        title="Username"
                        description="Change your username."
                        onClick={() => setShowUsernameModal(true)}
                    />
                    <ProfileSettingsButton
                        title="Name"
                        description="Set your name; this is usually what people call you."
                        onClick={() => setShowDisplaynameModal(true)}
                    />
                    <ProfileSettingsButton
                        title="Avatar"
                        description="Add or change your profile avatar."
                        onClick={() => setShowAvatarModal(true)}
                    />
                </div>

                {/* About Section */}
                <div className="form-control space-y-2">
                    <label className="form-label text-base">About</label>
                    <ProfileSettingsButton
                        title="Brief"
                        description="Add a short bio to your profile."
                        onClick={() => setShowBriefModal(true)}
                    />
                    <ProfileSettingsButton
                        title="Background"
                        description="Share information that shapes your perspective."
                        onClick={() => setShowBackgroundModal(true)}
                    />
                    <ProfileSettingsButton
                        title="Prioritea"
                        description="Rank your top issues in order of importance."
                        onClick={() => setShowIssueModal(true)}
                        settingValue={loadingRankings ? <Loader2 className="size-4 animate-spin" /> : issueCount}
                    />
                    <ProfileSettingsButton
                        title="Perspective"
                        description="View and respond to Teabates to share your perspective."
                        onClick={() => setShowPerspectiveModal(true)}
                    />
                    <ProfileSettingsButton
                        title="Links"
                        description="Add social media and other links."
                        onClick={() => setShowLinksModal(true)}
                        settingValue={currentUser?.properties?.about_bio_links?.value?.length || 0}
                    />
                </div>

                {/* Modals */}
                {showUsernameModal && (
                    <UsernameModal onClose={() => setShowUsernameModal(false)} />
                )}

                {showDisplaynameModal && (
                    <UserDisplaynameModal onClose={() => setShowDisplaynameModal(false)} />
                )}

                {showAvatarModal && (
                    <UserAvatarModal onClose={() => setShowAvatarModal(false)} />
                )}

                {showBriefModal && (
                    <UserAboutBriefModal onClose={() => setShowBriefModal(false)} />
                )}

                {showLinksModal && (
                    <UserAboutLinksModal onClose={() => setShowLinksModal(false)} />
                )}

                {showIssueModal && (
                    <UserAboutIssueModal onClose={() => setShowIssueModal(false)} />
                )}

                {showBackgroundModal && (
                    <UserAboutBackgroundModal isOpen={showBackgroundModal} onClose={() => setShowBackgroundModal(false)} initialData={currentUser?.properties} privacy={currentUser?.properties?.privacy_content_default_audience?.value || 'public'}/>
                )}
                {showPerspectiveModal && (
                    <UserPerspectiveForm isOpen={showPerspectiveModal} onClose={() => setShowPerspectiveModal(false)} />
                )}
        </div>
    )
}

export default UserSettingsProfile